import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Link,
  MessagePlugin,
  Popconfirm,
  Space,
  Table,
  Tooltip,
} from 'tdesign-react';
import { connect } from 'react-redux';
import { LockOnIcon } from 'tdesign-icons-react';
import { orderBy } from 'lodash';
import ListPanel from '@/components/ListPanel';
import PermWrapper from '@/components/PermWrapper';
import useTable from '@/hooks/useTable';
import { deleteOrganize, getOrganizeList, updateOrganize } from '@/services/organizeService';
import { renderDateTime, renderTags } from '@/utils/common';
import Editable from '@/components/Editable';
import { fetchClusterList, fetchUserListOptions } from '@/slices/organizeSlice';
import FormModal from './FormModal';
import UsersModal from './UsersModal';
import GrantModal from './GrantModal';

const { FormItem } = Form;

function Organize({ fetchUserListOptions, fetchClusterList }) {
  const [visible, setVisible] = useState(false);
  const [userVisible, setUserVisible] = useState(false);
  const [grantVisible, setGrantVisible] = useState(false);
  const [detail, setDetail] = useState({});

  const columns = [
    {
      title: '组名称',
      colKey: 'name',
      fixed: 'left',
      cell: ({ row }) => (
        <>
          {row.name}{' '}
          {row.isSystem === 1 && (
            <Tooltip content='系统内置'>
              <LockOnIcon />
            </Tooltip>
          )}
        </>
      ),
      width: 200,
    },
    {
      title: '组说明',
      colKey: 'desc',
      width: 300,
      cell: ({ row }) =>
        row.name === '白名单' ? (
          row.desc
        ) : (
          <Editable text={row.desc} onChange={(val) => onNoteChange(val, row)} />
        ),
    },
    {
      title: '组成员',
      colKey: 'account',
      cell: renderTags,
      width: 350,
    },
    {
      title: '创建时间',
      colKey: 'createTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      title: '更新时间',
      colKey: 'updateTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      cell: ({ row }) => (
        <Space>
          <PermWrapper resource='platform:organize:member'>
            <Link
              theme='primary'
              onClick={() => {
                setDetail(row);
                setUserVisible(true);
              }}
              hover='color'
            >
              成员管理
            </Link>
          </PermWrapper>
          {row.name === '白名单' ? null : (
            <>
              <PermWrapper resource='platform:organize:grant'>
                <Link
                  theme='primary'
                  onClick={() => {
                    setDetail(row);
                    setGrantVisible(true);
                  }}
                  hover='color'
                >
                  授权
                </Link>
              </PermWrapper>

              <PermWrapper resource='platform:organize:delete'>
                <Popconfirm theme='danger' content='确定删除？' onConfirm={() => doDelete(row)}>
                  <Link theme='danger' hover='color'>
                    删除
                  </Link>
                </Popconfirm>
              </PermWrapper>
            </>
          )}
        </Space>
      ),
      width: 180,
    },
  ];

  const doDelete = async (row) => {
    await deleteOrganize(row.id);
    MessagePlugin.success('操作成功');
    doRefresh();
  };

  const loadData = async (params) => {
    const { data } = await getOrganizeList(params);
    const _data = data.items?.map((item) => ({
      ...item,
      account: item.groupUserList?.map((item) => item.account),
      isSystem: item.name === '白名单' ? 1 : 0, // 系统内置
    }));

    setData(orderBy(_data, ['isSystem'], ['desc']), data.count);
  };

  const { config, doRefresh, header, setData, doSearch } = useTable({
    columns,
    onSearch: loadData,
    remote: true,
    search: (
      <FormItem name='name'>
        <Input clearable placeholder='请输入组名称' />
      </FormItem>
    ),
    operators: (
      <PermWrapper resource='platform:organize:create'>
        <Button onClick={() => setVisible(true)}>新增组</Button>
      </PermWrapper>
    ),
  });

  const handleClose = (refresh) => {
    setDetail(null);
    setVisible(false);
    setUserVisible(false);
    setGrantVisible(false);
    if (refresh) {
      doRefresh();
    }
  };

  useEffect(() => {
    doSearch();
    fetchUserListOptions();
    fetchClusterList();
  }, [doSearch, fetchUserListOptions, fetchClusterList]);

  const onNoteChange = async (desc, row) => {
    const params = {
      ...row,
      desc,
    };
    await updateOrganize(params);
    doRefresh();
  };

  return (
    <ListPanel header='组管理'>
      {header}
      <Table {...config} />
      <FormModal visible={visible} handleClose={handleClose} />
      <UsersModal visible={userVisible} handleClose={handleClose} detail={detail} />
      <GrantModal visible={grantVisible} handleClose={handleClose} groupId={detail?.id} />
    </ListPanel>
  );
}

export default connect(null, {
  fetchUserListOptions,
  fetchClusterList,
})(Organize);
